import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';

// Import your existing components
import GalleryViewer from './components/GalleryViewer';
import GallerySearch from './components/GallerySearch';
import GalleryDetail from './components/GalleryDetail';
import GalleryWallPlanner from './components/GalleryWallPlanner';
import ArtworkPdfGenerator from './components/ArtworkPdfGenerator';
import ArtistCardGenerator from './components/ArtistCardGenerator';
import ImprovedArtistDashboard from './components/ImprovedArtistDashboard';
import ArtistStudio from './components/ArtistStudio';
import ArtistDetailPage from './components/ArtistDetailPage';
import CollectionAdmin from './components/CollectionAdmin';
import UserDashboard from './components/UserManagement/UserDashboard';
import UserDetailPage from './components/UserManagement/UserDetailPage';
import Gallery3DEditor from './components/Gallery3DEditor';
import ArtworkAdmin from './components/ArtworkAdmin';
import ArtworkDetailPage from './components/ArtworkDetailPage';
import AudioArtworksAdmin from './components/AudioArtworksAdmin';
import AudioArtworksPublicPage from './components/AudioArtworksPublicPage';
import ArtSpeaksDirectory from './components/ArtSpeaksDirectory';

// Admin components
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
import AdminUnauthorized from './components/AdminUnauthorized';
import ArtworkEditPage from './components/admin/ArtworkEditPage';
import AddArtworkPage from './components/admin/AddArtworkPage';
import CollectionDetailAdmin from './components/CollectionDetailAdmin';
import EditSubmissionDetails from './components/curator/EditSubmissionDetails';
import EventDetailAdmin from './components/EventDetailAdmin';
import StoryViewPage from './components/StoryViewPage';
import StoryEditPage from './components/StoryEditPage';
import StoryCreationPage from './components/StoryCreationPage';
import UserProfileEdit from './components/UserProfileEdit';
import CreateUser from './components/UserManagement/CreateUser';
import GalleryAdmin from './components/GalleryAdmin';
import GalleryCreatePage from './components/GalleryCreatePage';
import AdminLayout from './components/AdminLayout';
import AdminSubmissions from './pages/AdminSubmissions';
import EventsAdmin from './components/EventsAdmin';
import NotificationManagementAdmin from './components/NotificationManagementAdmin';
import SendNotificationPage from './components/SendNotificationPage';
import SmartPulseAdmin from './components/SmartPulseAdmin';

// Public Pages (Flutter Route Compatibility)
import ArtworkPublicPage from './components/ArtworkPublicPage';
import ArtistPublicPage from './components/ArtistPublicPage';
import AppDownloadBanner from './components/AppDownloadBanner';
import IconTest from './components/IconTest';

// Event Check-in
import EventCheckin from './pages/EventCheckin';
import JudgeScoring from './pages/JudgeScoring';

// Artwork Acceptance
import ArtworkAcceptance from './pages/ArtworkAcceptance';

// Artist Submission System
import Login from './components/Login';
import Signup from './components/Signup';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import EmailOTPLogin from './components/EmailOTPLogin';
import ProtectedArtistRoute from './components/ProtectedArtistRoute';
import RedirectToArtistSubmission from './components/RedirectToArtistSubmission';
import ArtistSubmissionDashboard from './components/submissions/ArtistSubmissionDashboard';
import SubmissionWizard from './components/application/SubmissionWizard';
import SubmissionSuccess from './components/application/SubmissionSuccess';
import SubmissionDetails from './pages/SubmissionDetails';

// NEW: VibrantCast Homepage
import VibrantCastHomepage from './components/VibrantCastHomepage';

// Legal Pages
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';

// NEW: Dashboard components
import DashboardSelector from './components/DashboardSelector';
import DashboardDemo from './components/DashboardDemo';
import CuratorAdministrativeScreen from './components/CuratorAdministrativeScreen';
import CuratorLandingPage from './components/curator/CuratorLandingPage';
import OrganizationDetail from './components/curator/OrganizationDetail';
import OrganizationTeamManagement from './components/curator/OrganizationTeamManagement';
import CuratorCollections from './components/curator/CuratorCollections';
import CuratorCollectionDetail from './components/curator/CuratorCollectionDetail';
import CuratorGalleries from './components/curator/CuratorGalleries';
import CuratorGalleryDetail from './components/curator/CuratorGalleryDetail';
import CuratorCreateCollection from './components/curator/CuratorCreateCollection';
import CuratorCreateCollectionWizard from './components/curator/CuratorCreateCollectionWizard';
import CuratorCreateGallery from './components/curator/CuratorCreateGallery';
import CuratorEvents from './components/curator/CuratorEvents';
import EventCreatePage from './components/EventCreatePage';
import CuratorSubmissions from './components/curator/CuratorSubmissions';
import OrganizationTemplateManager from './components/curator/OrganizationTemplateManager';
import CuratorFeeWaivers from './components/curator/CuratorFeeWaivers';
import CuratorArtworks from './components/curator/CuratorArtworks';
import CuratorArtworkEdit from './components/curator/CuratorArtworkEdit';
import CuratorArtistsManagement from './components/curator/CuratorArtistsManagement';
import CuratorArtistDetail from './components/curator/CuratorArtistDetail';
import NewSubmission from './components/curator/NewSubmission';
import CuratorCommunicationsHub from './components/curator/CuratorCommunicationsHub';
import EmailTemplateEditor from './components/curator/EmailTemplateEditor';
import CuratorContacts from './components/curator/CuratorContacts';
import CuratorContactDetail from './components/curator/CuratorContactDetail';
import CuratorContactEmail from './components/curator/CuratorContactEmail';
import CuratorCollectionLayout from './components/curator/collections/v2/CuratorCollectionLayout';
import CuratorCollectionOverviewV2 from './components/curator/collections/v2/pages/Overview';
import CuratorCollectionArtworksV2 from './components/curator/collections/v2/pages/Artworks';
import CuratorCollectionArtistsV2 from './components/curator/collections/v2/pages/Artists';
import CuratorCollectionJudgingV2 from './components/curator/collections/v2/pages/Judging';
import CuratorEditCollection from './components/curator/CuratorEditCollection';
import CuratorTourEditor from './components/curator/CuratorTourEditor';
import CuratorEditEvent from './components/curator/CuratorEditEvent';
import CuratorSettings from './components/curator/CuratorSettings';
import CuratorBilling from './components/curator/CuratorBilling';
import CuratorServiceOrders from './components/curator/CuratorServiceOrders';
import CuratorServiceOrderDetail from './components/curator/CuratorServiceOrderDetail';
import CuratorPrintLabCustomers from './components/curator/CuratorPrintLabCustomers';
import CollectionArtistDetail from './components/curator/CollectionArtistDetail';
import PrintCampaignPage from './components/curator/PrintCampaignPage';
import PrintQualityPage from './components/curator/PrintQualityPage';
import SchedulingPage from './components/curator/SchedulingPage';

// Print Campaign - Artist Opt-in
import ArtistPrintOptinPage from './pages/ArtistPrintOptinPage';

// NEW: Basic placeholder components (we'll build these first)
import ArtistStudioHome from './components/artist/ArtistStudioHome';
import ArtistArtworksPage from './components/artist/ArtistArtworksPage';
import ArtistProfilePage from './components/artist/ArtistProfilePage';

// Placeholder components for other dashboards (simple for now)
const GalleryDashboard = () => (
  <div className="min-h-screen bg-gray-50 p-6">
    <h1 className="text-2xl font-bold mb-4">Gallery Dashboard</h1>
    <p>Coming soon! You have gallery admin access.</p>
    <a href="/admin/dashboard" className="text-blue-600 hover:underline">
      Go to Admin Dashboard →
    </a>
  </div>
);

const CollectorDashboard = () => (
  <div className="min-h-screen bg-gray-50 p-6">
    <h1 className="text-2xl font-bold mb-4">Collector Dashboard</h1>
    <p>Coming soon! Discover and collect amazing art.</p>
    <a href="/discover" className="text-blue-600 hover:underline">
      Discover Art →
    </a>
  </div>
);

const CuratorDashboard = () => (
  <div className="min-h-screen bg-gray-50 p-6">
    <h1 className="text-2xl font-bold mb-4">Curator Dashboard</h1>
    <p>Coming soon! Curate amazing exhibitions.</p>
    <a href="/events" className="text-blue-600 hover:underline">
      Browse Events →
    </a>
  </div>
);

import { supabase } from './utils/supabaseClient';
import './styles/App.css';
import './styles/ArtistDashboard.css';
import './styles/AdminDashboard.css';
import './styles/VibrantCastHomepage.css';

// Import route testing utility for FlutterFlow compatibility
import './utils/routeTest';

function App() {
  const [session, setSession] = useState(null);
  const [adminUser, setAdminUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Simple session check - completely non-blocking
  useEffect(() => {
    // Re-enabled with working timeout mechanism
    const checkSession = async () => {
      try {
        // Add a working timeout to prevent hanging
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout
        
        try {
          const { data: { session } } = await supabase.auth.getSession();
          clearTimeout(timeoutId);
          setSession(session);
          
          if (session?.user) {
            // Use RPC function to check admin access properly
            try {
              const { data, error } = await supabase.rpc('get_user_details_basic', {
                user_uuid: session.user.id
              });

              if (!error && data?.user_details?.isAdmin) {
                const { data: userData } = await supabase
                  .from('users')
                  .select('*')
                  .eq('uuid', session.user.id)
                  .single();
                
                if (userData) {
                  setAdminUser({ ...userData, role: 'Company Admin' });
                }
              }
            } catch (adminError) {
              console.error('Admin check error:', adminError);
              // Don't block the app if admin check fails
            }
          }
        } catch (error) {
          clearTimeout(timeoutId);
          if (error.name === 'AbortError') {
            console.log('Session check timed out, continuing without session');
            setSession(null);
            setAdminUser(null);
          } else {
            throw error;
          }
        }
      } catch (error) {
        console.error('Session check error:', error);
        // Don't block the app if session check fails
        setSession(null);
        setAdminUser(null);
      }
    };

    // Run session check in background without blocking
    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        if (event === 'SIGNED_OUT') {
          setAdminUser(null);
          navigate('/');
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [navigate]);

  return (
    <>
      <Routes>
        {/* Public Marketing Routes */}
        <Route path="/" element={<VibrantCastHomepage />} />
        <Route path="/test-icons" element={<IconTest />} />
        <Route path="/artists" element={<div>Artists Page (Coming Soon)</div>} />
        <Route path="/collectors" element={<div>Collectors Page (Coming Soon)</div>} />
        <Route path="/galleries" element={<div>Galleries Page (Coming Soon)</div>} />
        <Route path="/about" element={<div>About Page (Coming Soon)</div>} />
        <Route path="/contact" element={<div>Contact Page (Coming Soon)</div>} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        
        {/* CRITICAL: Flutter Route Compatibility - Preserve SEO */}
        {/* Public artwork page: /feelArtStory?artId=3383 */}
        <Route path="/feelArtStory" element={<ArtworkPublicPage />} />
        {/* Public artist page: /artist?handle=lazaro-world-tour */}
        <Route path="/artist" element={<ArtistPublicPage />} />
        
        {/* Modern alternative routes (optional) */}
        <Route path="/artwork/:vcId" element={<ArtworkDetailPage />} />
        <Route path="/artist/:handle" element={<ArtistPublicPage />} />
        
        {/* Additional artwork detail routes */}
        <Route path="/artworks/:vcId" element={<ArtworkDetailPage />} />
        <Route path="/curator/artworks/:vcId" element={<ArtworkDetailPage />} />
        
        {/* Audio-enhanced artworks */}
        <Route path="/audio-artworks" element={<AudioArtworksPublicPage />} />
        
        {/* Additional FlutterFlow route compatibility */}
        <Route path="/artStory" element={<ArtworkPublicPage />} />
        <Route path="/feelArtwork" element={<ArtworkPublicPage />} />
        <Route path="/galleryArtworkDetails" element={<ArtworkPublicPage />} />
        <Route path="/artworkDetails" element={<ArtworkPublicPage />} />
        
        {/* Artist portfolio routes */}
        <Route path="/artistFullPortfolio" element={<ArtistPublicPage />} />
        <Route path="/artistPortfolio" element={<ArtistPublicPage />} />
        
        {/* Collection and gallery routes */}
        <Route path="/curatedCollections" element={<div className="p-6"><h2 className="text-xl font-bold">Curated Collections</h2><p>Coming soon!</p></div>} />
        <Route path="/nearbyGalleries" element={<div className="p-6"><h2 className="text-xl font-bold">Nearby Galleries</h2><p>Coming soon!</p></div>} />
        <Route path="/location" element={<div className="p-6"><h2 className="text-xl font-bold">Location</h2><p>Coming soon!</p></div>} />
        
        {/* Event and story routes */}
        <Route path="/events" element={<div className="p-6"><h2 className="text-xl font-bold">Events</h2><p>Coming soon!</p></div>} />
        <Route path="/event" element={<div className="p-6"><h2 className="text-xl font-bold">Event Details</h2><p>Coming soon!</p></div>} />
  <Route path="/artSpeaks" element={<ArtSpeaksDirectory />} />
        <Route path="/pulse" element={<div className="p-6"><h2 className="text-xl font-bold">Pulse</h2><p>Coming soon!</p></div>} />
        
        {/* Search and discovery routes */}
        <Route path="/artistSearch" element={<div className="p-6"><h2 className="text-xl font-bold">Artist Search</h2><p>Coming soon!</p></div>} />
        <Route path="/discoverArtwork" element={<div className="p-6"><h2 className="text-xl font-bold">Discover Artwork</h2><p>Coming soon!</p></div>} />
        <Route path="/artworkNearMe" element={<div className="p-6"><h2 className="text-xl font-bold">Artwork Near Me</h2><p>Coming soon!</p></div>} />
        <Route path="/localArtists" element={<div className="p-6"><h2 className="text-xl font-bold">Local Artists</h2><p>Coming soon!</p></div>} />
        
        {/* Content and media routes */}
        <Route path="/content" element={<div className="p-6"><h2 className="text-xl font-bold">Content</h2><p>Coming soon!</p></div>} />
        <Route path="/contentList" element={<div className="p-6"><h2 className="text-xl font-bold">Content List</h2><p>Coming soon!</p></div>} />
        <Route path="/videoStory" element={<div className="p-6"><h2 className="text-xl font-bold">Video Story</h2><p>Coming soon!</p></div>} />
        <Route path="/artPrints" element={<div className="p-6"><h2 className="text-xl font-bold">Art Prints</h2><p>Coming soon!</p></div>} />
        
        {/* Artist Print Opt-in (public, accessed via magic link) */}
        <Route path="/prints/optin/:optinUuid" element={<ArtistPrintOptinPage />} />
        
        {/* Utility and special routes */}
        <Route path="/passport" element={<div className="p-6"><h2 className="text-xl font-bold">Passport</h2><p>Coming soon!</p></div>} />
        <Route path="/shareQR" element={<div className="p-6"><h2 className="text-xl font-bold">Share QR</h2><p>Coming soon!</p></div>} />
        <Route path="/pinnedBoard" element={<div className="p-6"><h2 className="text-xl font-bold">Pinned Board</h2><p>Coming soon!</p></div>} />
        <Route path="/findCollection" element={<div className="p-6"><h2 className="text-xl font-bold">Find Collection</h2><p>Coming soon!</p></div>} />
        
        {/* Public Gallery Editor */}
        <Route path="/gallery-3d-editor" element={<Gallery3DEditor viewMode="3d" />} />
        
        {/* Authentication Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/email-login" element={<EmailOTPLogin />} />
        
        {/* NEW: Dashboard Selector */}
        <Route path="/dashboard-selector" element={<DashboardSelector />} />
        <Route path="/dashboard-demo" element={<DashboardDemo />} />
        
        {/* NEW: Artist Dashboard Routes */}
        <Route path="/artist/*" element={
          <ProtectedArtistRoute>
            <ArtistStudio />
          </ProtectedArtistRoute>
        }>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<ArtistStudioHome />} />
          <Route path="submissions" element={<ArtistSubmissionDashboard />} />
          <Route path="submissions/:submissionId/apply" element={<SubmissionWizard />} />
          <Route path="submissions/:submissionId/success" element={<SubmissionSuccess />} />
          {/* Placeholder routes for now */}
          <Route path="artworks" element={<ArtistArtworksPage />} />
          <Route path="artwork/:vcId" element={<ArtworkDetailPage />} />
          <Route path="upload" element={<div className="p-6"><h2 className="text-xl font-bold">Upload Artwork</h2><p>Coming soon!</p></div>} />
          <Route path="inquiries" element={<div className="p-6"><h2 className="text-xl font-bold">Inquiries</h2><p>Coming soon!</p></div>} />
          <Route path="profile" element={<ArtistProfilePage />} />
          <Route path="preferences" element={<div className="p-6"><h2 className="text-xl font-bold">Preferences</h2><p>Coming soon!</p></div>} />
        </Route>

        {/* NEW: Other Dashboard Routes (simple placeholders) */}
        <Route path="/gallery/dashboard" element={<GalleryDashboard />} />
        <Route path="/collector/dashboard" element={<CollectorDashboard />} />
        <Route path="/curator/*" element={<CuratorAdministrativeScreen />}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<CuratorLandingPage />} />
          <Route path="galleries" element={<CuratorGalleries />} />
          <Route path="galleries/:galleryId" element={<CuratorGalleryDetail />} />
          <Route path="galleries/:galleryId/create-collection" element={<CuratorCreateCollection />} />
          <Route path="collections" element={<CuratorCollections />} />
          <Route path="collections/:collectionId" element={<CuratorCollectionDetail />} />
          <Route path="collections/:collectionId/prints" element={<PrintCampaignPage />} />
          <Route path="collections/:collectionId/scheduling" element={<SchedulingPage />} />
          <Route path="collections/:collectionId/edit" element={<CuratorEditCollection />} />
          <Route path="collections/:collectionId/tour" element={<CuratorTourEditor />} />
          <Route path="collections/:collectionId/artists/:artistUuid" element={<CollectionArtistDetail />} />
          {/* Curator v2 routes - new, non-destructive */}
          <Route path="v2/collections/:collectionId" element={<CuratorCollectionLayout />}>
            <Route index element={<CuratorCollectionOverviewV2 />} />
            <Route path="overview" element={<CuratorCollectionOverviewV2 />} />
            <Route path="artworks" element={<CuratorCollectionArtworksV2 />} />
            <Route path="artists" element={<CuratorCollectionArtistsV2 />} />
            <Route path="judging" element={<CuratorCollectionJudgingV2 />} />
          </Route>
          <Route path="collections/:collectionId/edit-submission" element={<EditSubmissionDetails />} />
          <Route path="collections/new" element={<CuratorCreateCollectionWizard />} />
          <Route path="galleries/new" element={<CuratorCreateGallery />} />
          <Route path="events" element={<CuratorEvents />} />
          <Route path="events/:eventId" element={<EventDetailAdmin />} />
          <Route path="events/:eventId/edit" element={<CuratorEditEvent />} />
          <Route path="events/:eventId/stories/create" element={<StoryCreationPage />} />
          <Route path="events/new" element={<EventCreatePage />} />
          <Route path="stories/:storyId" element={<StoryViewPage />} />
          <Route path="stories/:storyId/edit" element={<StoryEditPage />} />
          <Route path="submissions" element={<CuratorSubmissions />} />
          <Route path="submissions/new" element={<NewSubmission />} />
          <Route path="submission-template" element={<OrganizationTemplateManager />} />
          <Route path="fee-waivers" element={<CuratorFeeWaivers />} />
          <Route path="artists" element={<CuratorArtistsManagement />} />
          <Route path="artists/:artistId" element={<CuratorArtistDetail />} />
          <Route path="artworks/:vcId/edit" element={<CuratorArtworkEdit />} />
          <Route path="artworks/:vcId/print" element={<PrintQualityPage />} />
          <Route path="artworks" element={<CuratorArtworks />} />
          <Route path="communications" element={<CuratorCommunicationsHub />} />
          <Route path="communications/templates/new" element={<EmailTemplateEditor />} />
          <Route path="communications/templates/:templateId/edit" element={<EmailTemplateEditor />} />
          <Route path="contacts" element={<CuratorContacts />} />
          <Route path="contacts/email" element={<CuratorContactEmail />} />
          <Route path="contacts/:contactId" element={<CuratorContactDetail />} />
          <Route path="organization/:orgSlug" element={<OrganizationDetail />} />
          <Route path="organization/:orgSlug/team" element={<OrganizationTeamManagement />} />
          <Route path="settings" element={<CuratorSettings />} />
          <Route path="billing" element={<CuratorBilling />} />
          <Route path="service-orders" element={<CuratorServiceOrders />} />
          <Route path="service-orders/:orderId" element={<CuratorServiceOrderDetail />} />
          <Route path="customers" element={<CuratorPrintLabCustomers />} />
        </Route>

        {/* Submission Details (public view) */}
        <Route path="/submissions/:submissionId" element={<SubmissionDetails />} />
        
        {/* Artwork Acceptance Page */}
        <Route path="/acceptance/:acceptanceUuid" element={<ArtworkAcceptance />} />
        
        {/* Redirect old submission apply URLs to new artist dashboard structure */}
        <Route path="/submissions/:submissionId/apply" element={<RedirectToArtistSubmission action="apply" />} />
        <Route path="/submissions/:submissionId/success" element={<RedirectToArtistSubmission action="success" />} />
        
        {/* Admin Login Routes - Now handled by regular /login with smart routing */}
        <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
        
        {/* Admin Routes - All wrapped in UnifiedDashboardLayout */}
        <Route path="/admin/users/:userId" element={<AdminDashboard />} />
        <Route path="/admin/users/:userId/edit" element={<AdminDashboard />} />
        <Route path="/admin/users/create" element={<AdminDashboard />} />
        <Route path="/admin/artworks/:vcId/edit" element={<CuratorArtworkEdit />} />
        <Route path="/admin/users/:userId/add-artwork" element={<AddArtworkPage />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/enrichment" element={<AdminDashboard />} />
        <Route path="/admin/users" element={<AdminDashboard />} />
        <Route path="/admin/prospects" element={<AdminDashboard />} />
        <Route path="/admin/artists" element={<AdminDashboard />} />
        <Route path="/admin/galleries" element={<AdminDashboard />} />
        <Route path="/admin/galleries/create" element={<AdminDashboard />} />
        <Route path="/admin/collections" element={<AdminDashboard />} />
        <Route path="/admin/collections/:collectionId" element={<AdminDashboard />} />
        <Route path="/admin/submissions" element={<AdminDashboard />} />
        <Route path="/admin/open-calls" element={<AdminDashboard />} />
        <Route path="/admin/open-calls/create" element={<AdminDashboard />} />
        <Route path="/admin/open-calls/:calloutId/edit" element={<AdminDashboard />} />
        <Route path="/admin/scraped-calls" element={<Navigate to="/admin/open-calls" replace />} />
        <Route path="/admin/fee-waivers" element={<AdminDashboard />} />
        <Route path="/admin/organizations" element={<AdminDashboard />} />
        <Route path="/admin/channels" element={<AdminDashboard />} />
        <Route path="/admin/events" element={<AdminDashboard />} />
        <Route path="/admin/analytics" element={<AdminDashboard />} />
        <Route path="/admin/notifications" element={<AdminDashboard />} />
        <Route path="/admin/notifications/send" element={<SendNotificationPage />} />
        <Route path="/admin/settings" element={<AdminDashboard />} />
        <Route path="/admin/events/:eventId" element={<AdminDashboard />} />
        <Route path="/admin/stories/:storyId" element={<AdminDashboard />} />
        <Route path="/admin/stories/:storyId/edit" element={<AdminDashboard />} />
        <Route path="/admin/stories/create" element={<AdminDashboard />} />
        <Route path="/admin/events/:eventId/stories/create" element={<AdminDashboard />} />
        <Route path="/admin/pulse" element={<AdminDashboard />} />
        <Route path="/admin/app-content" element={<AdminDashboard />} />
        <Route path="/admin/art-stories" element={<AdminDashboard />} />
        <Route path="/admin/writers" element={<AdminDashboard />} />

        {/* Artwork Management */}
        <Route path="/admin/artworks" element={<AdminDashboard />} />
        <Route path="/admin/artworks/:vcId" element={<AdminDashboard />} />
        <Route path="/admin/audio-artworks" element={<AudioArtworksAdmin />} />
        
        {/* Orders Management */}
        <Route path="/admin/orders" element={<AdminDashboard />} />
        
  {/* Story routes */}
  <Route path="/stories/create" element={<StoryCreationPage />} />
  <Route path="/stories/:storyId" element={<StoryViewPage />} />

  {/* Event Check-in Routes */}
        <Route path="/event/:eventId/checkin" element={<EventCheckin />} />
        
        {/* Public Judge Scoring Page */}
        <Route path="/judge/:roundId" element={<JudgeScoring />} />
        
        {/* Legacy Admin Routes */}
        <Route path="/admin/artists-new" element={<AdminLayout><ArtistDetailPage /></AdminLayout>} />
        <Route path="/admin/artist/:artistId" element={<AdminLayout><ArtistDetailPage /></AdminLayout>} />
        
        {/* Your existing Gallery and Public Routes (keep exactly as they are) */}
        <Route path="/galleries" element={<GallerySearch />} />
        <Route path="/gallery/:id" element={<GalleryDetail />} />
        <Route path="/gallery-3d" element={<Gallery3DEditor viewMode="3d" />} />
        <Route path="/gallery" element={<GallerySearch />} />
        <Route path="/gallery-view" element={<GalleryViewer />} />
        <Route path="/gallery-wall-planner" element={<GalleryWallPlanner />} />
        <Route path="/gallery-wall-planner/:collectionId" element={<GalleryWallPlanner />} />
        <Route path="/printArtistCards/:collectionId" element={<ArtistCardGenerator />} />
        <Route path="/printReactCards/:collectionId" element={<ArtworkPdfGenerator />} />
        
        {/* Keep your existing catch-all */}
        <Route path="*" element={<VibrantCastHomepage />} />
      </Routes>
      
      {/* App Download Banner - Temporarily disabled */}
      {/* <AppDownloadBanner /> */}
    </>
  );
}

export default App;